import React, {
  Dispatch,
  MutableRefObject,
  SetStateAction,
  useCallback,
  useEffect,
  useRef,
} from 'react';
import { useCustomerProvider } from '@material-bank/customer';
import { useCurrentProject } from '@material-bank/projects-utils';
import { useIntl } from 'react-intl';
import { useMediaQuery } from 'react-responsive';
import { useHeaderClickaway } from './useHeaderClickaway';
import { useLabels } from './useLabels';
import {
  calculateBackdropPadding,
  prepareBannerContent,
  getControlRuleConfig,
  formatRecommendedItems,
  sendAnalyticsEvents,
  determineServiceType,
  prepareServiceBannerContent,
  createServiceProposal,
} from './useRecommendationsModalListeners.utils';
import {
  getIsInStockAllowed,
  getIsTierAllowed,
  getRecommendationsPerSku,
  getTimeBetweenIterations,
} from '../api/localStorageCalls';
import {
  checkMaterialAvailability,
  getRecommendations,
} from '../api/recommendations';
import {
  ModalProps,
  OpenRecomendationsModalPayload,
  OpenServicesModalPayload,
} from '../types/recommendationsModal.types';

export const CUSTOM_OPEN_EVENT_NAME = 'open-recommendations-modal';
export const SERVICE_OPEN_EVENT_NAME = 'open-services-modal';
export const CUSTOM_CLOSE_EVENT_NAME = 'close-recommendations-modal';

export const isInStockAllowed = getIsInStockAllowed();
export const isTierAllowed = getIsTierAllowed();
export const timeBetweenIterations = getTimeBetweenIterations() || 4000;
export const recommendationsPerSku = getRecommendationsPerSku() || Infinity;

type UseRecommendationsModalListenersArgs = {
  isOpen: boolean;
  textLabels: ReturnType<typeof useLabels>['textLabels'];
  closeTimeoutRef: MutableRefObject<NodeJS.Timeout | null>;
  setModalProps: Dispatch<SetStateAction<ModalProps>>;
  setIsOpen: Dispatch<SetStateAction<boolean>>;
  setIsViewEventTriggered: Dispatch<SetStateAction<boolean>>;
  setProposal: Dispatch<SetStateAction<Record<string, any> | null>>;
  setXHeader: Dispatch<SetStateAction<string | null>>;
  handleCloseClick: () => void;
};

export function useRecommendationsModalListeners({
  isOpen,
  textLabels,
  closeTimeoutRef,
  setModalProps,
  setIsOpen,
  setIsViewEventTriggered,
  setProposal,
  setXHeader,
  handleCloseClick,
}: UseRecommendationsModalListenersArgs) {
  const { customer } = useCustomerProvider();
  const { currentProject } = useCurrentProject();
  const isMobile = useMediaQuery({ query: '(max-width: 767px)' });
  const dealerName = useRef<string | null>(null);
  const intl = useIntl();

  // Track if an event is currently being processed to prevent race conditions
  const isProcessingEventRef = useRef<boolean>(false);

  // Enhanced close handler that resets processing flag
  const closeHandler = useCallback(() => {
    isProcessingEventRef.current = false;
    handleCloseClick();
  }, [handleCloseClick]);

  // Handle header clickaway functionality
  useHeaderClickaway(isOpen, closeHandler);

  useEffect(() => {
    const handleDealerNameChange = (
      e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    ) => {
      const newValue = e.target.value;
      dealerName.current = newValue;

      setProposal((prevProposal) => ({
        ...prevProposal,
        isAddDealerButtonLoading: !(newValue?.length > 0),
      }));
    };

    const eventListener: EventListenerOrEventListenerObject = async (
      event: CustomEvent<OpenRecomendationsModalPayload>,
    ) => {
      // Ignore new events if modal is already open or if another event is being processed
      if (isOpen || isProcessingEventRef.current) {
        return;
      }

      // Mark that we're processing an event
      isProcessingEventRef.current = true;

      const detail = event?.detail;
      const controlRuleId = detail?.controlRuleId ?? null;
      const isForceAllow = detail?.forceAllow || false;
      const isServerMessage = detail.serverMessage != null;
      const shouldHideRecommendations =
        detail.shouldHideRecommendations ?? false;

      const isBrandDailyLimitReached =
        detail?.guardrailsDailyLimitReached ?? false;

      // Checks if ships overnight
      const isInRealTimeStock =
        detail.isInRealTimeStock && !isBrandDailyLimitReached;

      const shouldShowRecommendations =
        (isInStockAllowed ? true : !isInRealTimeStock) &&
        isTierAllowed &&
        !shouldHideRecommendations;

      if (
        !isForceAllow &&
        (isMobile ||
          detail.inProgress ||
          !isTierAllowed ||
          (!isServerMessage && isInRealTimeStock && !isInStockAllowed))
      ) {
        // Reset the flag
        isProcessingEventRef.current = false;

        return;
      }

      const backdropTopPadding = calculateBackdropPadding();
      const { bannerLabel, bannerShippingTitle, bannerShippingSubtitle } =
        prepareBannerContent(
          detail,
          isInRealTimeStock,
          isServerMessage,
          textLabels,
        );

      const recommendVariant = !isInRealTimeStock ? 'oos' : undefined;
      const { recommendedItems, xHeader } = await getRecommendations(
        detail.skus,
        'id',
        shouldShowRecommendations,
        recommendationsPerSku,
        recommendVariant,
        {},
        currentProject,
      );
      setXHeader(String(xHeader));

      const recommendedItemsEntityIds = recommendedItems?.map(
        (item: Record<string, any>) => item.entity_id,
      );
      const availableMaterials = await checkMaterialAvailability(
        recommendedItemsEntityIds,
      );

      const availableMaterialsEntityIds = availableMaterials
        ?.filter((item) => Number(item.qty) > 0)
        .map((availableMaterial) => String(availableMaterial.product_id));

      const controlRule = getControlRuleConfig(
        controlRuleId,
        isBrandDailyLimitReached,
      );

      const controlRuleItemListId =
        controlRule?.list_id_suffix != null
          ? `sim_${controlRule?.list_id_suffix}`
          : 'sim_new_control_rule';

      const controlRuleItemListName =
        controlRule?.list_name_prefix != null
          ? `${controlRule?.list_name_prefix} SIM`
          : 'New Control Rule SIM';

      const itemListId = isServerMessage ? controlRuleItemListId : 'sim_oos';
      const itemListName = isServerMessage
        ? controlRuleItemListName
        : 'Out of stock SIM';

      const formattedRecommendations = formatRecommendedItems(
        recommendedItems,
        availableMaterialsEntityIds,
        itemListId,
        itemListName,
      );
      const slicedRecommendations = formattedRecommendations.slice(
        0,
        recommendationsPerSku,
      );

      // Send analytics events
      sendAnalyticsEvents(
        detail,
        isInRealTimeStock,
        isServerMessage,
        controlRuleId,
        isBrandDailyLimitReached,
        customer,
      );

      // If the close timeout persists it means that the previous iteration
      // has not been fulfilled, and we shall not trigger a new modal
      setIsOpen(true);

      setModalProps((prevProps) => ({
        ...prevProps,
        recommendedItems: slicedRecommendations,
        bagProject: detail.bagProject,
        addedMaterialImage: detail.addedMaterialImage,
        backdropTopPadding,
        bannerLabel,
        bannerShippingTitle,
        bannerShippingSubtitle,
        controlRuleId,
        isBrandDailyLimitReached,
      }));

      // Reset processing flag after modal is opened
      isProcessingEventRef.current = false;

      if (closeTimeoutRef.current == null) {
        closeTimeoutRef.current = setTimeout(() => {
          setIsOpen(false);
          setIsViewEventTriggered(false);
          setModalProps((prevProps) => ({
            ...prevProps,
            isInitMaterialAddedToBag: false,
            controlRuleId: null,
            addedMaterialImage: !prevProps?.controlRuleId
              ? prevProps?.addedMaterialImage
              : null,
          }));
          setProposal(null);
          closeTimeoutRef.current = null;
          // Reset processing flag when timeout closes modal
          isProcessingEventRef.current = false;
        }, timeBetweenIterations);
      }
    };

    const serviceEventListener: EventListenerOrEventListenerObject = async (
      event: CustomEvent<OpenServicesModalPayload>,
    ) => {
      // Ignore new events if modal is already open or if another event is being processed
      if (isOpen || isProcessingEventRef.current) {
        return;
      }

      // Mark that we're processing an event
      isProcessingEventRef.current = true;

      const detail = event?.detail;
      const isServerMessage = detail.serverMessage != null;
      const isInRealTimeStock = detail.isInRealTimeStock;
      const services = detail.service;

      // Calculate backdrop padding using utility function
      const backdropTopPadding = calculateBackdropPadding();

      // Prepare banner content
      const { bannerLabel, bannerShippingTitle, bannerShippingSubtitle } =
        prepareServiceBannerContent(
          detail,
          isInRealTimeStock,
          isServerMessage,
          textLabels,
        );

      // Update modal state
      setIsOpen(true);
      setModalProps((prevProps) => ({
        ...prevProps,
        recommendedItems: [],
        bagProject: detail.bagProject,
        addedMaterialImage: detail.addedMaterialImage,
        backdropTopPadding,
        bannerLabel,
        bannerShippingTitle,
        bannerShippingSubtitle,
      }));

      // Reset processing flag after modal is opened
      isProcessingEventRef.current = false;

      // Handle service proposal if services exist
      if (services != null) {
        const serviceType = determineServiceType(services);
        const proposal = createServiceProposal(services, serviceType, detail, {
          handleCloseClick,
          setProposal,
          handleDealerNameChange,
          dealerName,
        });

        // Add common properties
        const completeProposal = {
          ...proposal,
          companyImage:
            services?.data?.popup_image || services?.data?.product_image || '',
          productSku: services?.data?.roduct_sku,
        };

        setProposal(completeProposal);
      }
    };

    window.addEventListener(CUSTOM_OPEN_EVENT_NAME, eventListener);
    window.addEventListener(SERVICE_OPEN_EVENT_NAME, serviceEventListener);
    window.addEventListener(CUSTOM_CLOSE_EVENT_NAME, closeHandler);

    return () => {
      window.removeEventListener(CUSTOM_OPEN_EVENT_NAME, eventListener);
      window.removeEventListener(SERVICE_OPEN_EVENT_NAME, serviceEventListener);
      window.removeEventListener(CUSTOM_CLOSE_EVENT_NAME, closeHandler);
    };
  }, [
    isMobile,
    isOpen,
    intl,
    textLabels,
    currentProject,
    customer,
    closeTimeoutRef,
    handleCloseClick,
    closeHandler,
    setXHeader,
    setIsOpen,
    setModalProps,
    setIsViewEventTriggered,
    setProposal,
  ]);
}
